import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./App.css";
import axios from "axios";
import { toast } from "sonner";
import {
  ScanSearch, Loader2, Sparkles, Crosshair, Wand2,
  Plus, Library, PlayCircle, X, ExternalLink, FileText, Share2, GitCompareArrows, AlertTriangle, Settings, Mail,
} from "lucide-react";
import ResultView from "./components/ResultView";
import EmailModal from "./components/EmailModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const loadBranding = () => {
  try { return JSON.parse(localStorage.getItem("lexgap_branding") || "{}"); } catch (e) { return {}; }
};

function App() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [context, setContext] = useState("");
  const [sector, setSector] = useState("general");
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);
  const [recent, setRecent] = useState([]);
  const [showBench, setShowBench] = useState(false);
  const [showBrand, setShowBrand] = useState(false);
  const [emailFor, setEmailFor] = useState(null);
  const [compareSel, setCompareSel] = useState([]);
  const [branding, setBranding] = useState(loadBranding);
  const [firmDraft, setFirmDraft] = useState(branding.firm || "");

  const loadRecent = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/analyses`); setRecent(data || []); } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => {
    loadRecent();
    axios.get(`${API}/sectors`).then(({ data }) => setSectors(data || [])).catch(() => {});
  }, [loadRecent]);

  const sectorLabel = (sectors.find((s) => s.key === sector) || {}).label || "General";
  const labelForKey = (k) => (sectors.find((s) => s.key === (k || "general")) || {}).label || "General";
  const benchmarks = (sectors.find((s) => s.key === sector) || {}).benchmarks || [];
  const firm = (branding.firm || "").trim();

  const newCaseAudit = () => {
    setTitle(""); setContext(""); setText(""); setResult(null); setError(""); setAnalysisId(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const loadDemo = async () => {
    setError("");
    try {
      const { data } = await axios.get(`${API}/example/crosssector`);
      setTitle(data.title); setContext(data.context); setText(data.text); setSector("healthcare");
    } catch (e) { setError("Could not load the demo. Please try again."); }
  };

  const detectSector = async () => {
    if (text.trim().length < 80) { toast.error("Paste a paragraph first so LexGap can detect the sector."); return; }
    setDetecting(true);
    try {
      const { data } = await axios.post(`${API}/detect-sector`, { text });
      setSector(data.sector);
      toast.success(`Suggested sector: ${data.label} (${data.confidence} confidence)`, { description: data.rationale });
    } catch (e) {
      toast.error("Could not detect the sector. Please pick one manually.");
    } finally { setDetecting(false); }
  };

  const analyze = async () => {
    setError(""); setResult(null); setAnalysisId(null); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/analyze`, { text, title: title || "Untitled decision", context, sector, firm });
      const jobId = data.job_id;
      const started = Date.now();
      const poll = async () => {
        try {
          const { data: job } = await axios.get(`${API}/analyze/${jobId}`);
          if (job.status === "done") { setResult(job.result); setAnalysisId(job.analysis_id || null); setLoading(false); loadRecent(); return; }
          if (job.status === "error") { setError(job.error || "Analysis failed."); setLoading(false); return; }
          if (Date.now() - started > 180000) { setError("Analysis timed out — please try again."); setLoading(false); return; }
          setTimeout(poll, 3000);
        } catch (e) { setError("Lost connection while analysing — please try again."); setLoading(false); }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Analysis failed. Please try again.");
      setLoading(false);
    }
  };

  const openRecent = async (id) => {
    setError(""); setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/analyses/${id}`);
      setResult(data.result); setAnalysisId(id); setSector(data.sector || "general"); setTitle(data.title || "");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { setError("Could not open that analysis."); } finally { setLoading(false); }
  };

  const share = async (id) => {
    const url = `${window.location.origin}/report/${id}`;
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied to clipboard"); }
    catch (e) { toast.message("Share link", { description: url }); }
  };

  const saveBranding = () => {
    const b = { firm: firmDraft.trim() };
    localStorage.setItem("lexgap_branding", JSON.stringify(b));
    setBranding(b); setShowBrand(false);
    toast.success("Branding saved — applied to your new audits");
  };

  const toggleCompare = (id) => {
    setCompareSel((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].slice(-3)));
  };

  const r = result || {};

  return (
    <div className="fl-app">
      <div className="fl-top">
        <div className="fl-wrap">
          <div className="fl-brandrow">
            <div className="fl-brand">
              <Crosshair size={28} color="#c9a24b" />
              <div>
                <div className="fl-mark">LEX<span>GAP</span></div>
                <div className="fl-domain">lexgap.app</div>
              </div>
            </div>
            <div className="fl-nav">
              <button className="fl-btn fl-btn--ghost fl-btn--sm" data-testid="new-case-audit-btn" onClick={newCaseAudit}><Plus size={15} /> New Case Audit</button>
              <button className="fl-btn fl-btn--ghost fl-btn--sm" data-testid="view-benchmarks-btn" onClick={() => setShowBench(true)}><Library size={15} /> View Benchmarks</button>
              <button className="fl-btn fl-btn--ghost fl-btn--sm" data-testid="branding-btn" onClick={() => { setFirmDraft(branding.firm || ""); setShowBrand(true); }}><Settings size={15} /> Branding</button>
            </div>
          </div>
          <p className="fl-tag">
            Paste any decision that feels unfair. LexGap stress-tests it against the law that
            <b> should</b> have applied and returns <b>either</b> evidenced routes to challenge it,
            <b> or</b> a reasoned confirmation that it is sound.
          </p>
        </div>
      </div>

      <div className="fl-wrap">
        <div className="fl-card" data-testid="input-card">
          <div className="fl-row">
            <div>
              <label className="fl-label">Case / decision title</label>
              <input className="fl-input" data-testid="input-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Costs determination — professional-conduct tribunal" />
            </div>
            <div>
              <label className="fl-label">Context / jurisdiction (optional)</label>
              <input className="fl-input" data-testid="input-context" value={context} onChange={(e) => setContext(e.target.value)} placeholder="e.g. Disciplinary tribunal, England & Wales" />
            </div>
          </div>
          <div className="fl-selectwrap">
            <label className="fl-label">Sector preset <span className="fl-labelhint">tunes which benchmarks the audit weighs</span></label>
            <div className="fl-selectrow">
              <select className="fl-select" data-testid="sector-select" value={sector} onChange={(e) => setSector(e.target.value)}>
                {sectors.length ? sectors.map((s) => <option key={s.key} value={s.key}>{s.label}</option>) : <option value="general">General</option>}
              </select>
              <button className="fl-btn fl-btn--ghost fl-btn--sm" data-testid="detect-sector-btn" onClick={detectSector} disabled={detecting || text.trim().length < 80}>
                {detecting ? <><Loader2 size={15} className="fl-spin-inline" /> Detecting…</> : <><Wand2 size={15} /> Suggest</>}
              </button>
            </div>
          </div>
          <label className="fl-label">Decision text</label>
          <textarea className="fl-textarea" data-testid="input-decision-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste the judgment, ruling or determination here (a paragraph minimum)…" />
          <div className="fl-btns">
            <button className="fl-btn fl-btn--primary" data-testid="analyze-btn" onClick={analyze} disabled={loading || text.trim().length < 80}>
              {loading ? <><Loader2 size={16} className="fl-spin-inline" /> Analysing…</> : <><Sparkles size={16} /> Run forensic analysis</>}
            </button>
            <button className="fl-btn fl-btn--ghost" data-testid="try-demo-btn" onClick={loadDemo} disabled={loading}><PlayCircle size={16} /> Try a demo</button>
            <span className="fl-hint">Analysis takes ~30–120s · powered by Claude Sonnet 4.6</span>
          </div>
        </div>

        {error && <div className="fl-error" data-testid="error-banner"><AlertTriangle size={15} style={{ verticalAlign: "-2px", marginRight: 8 }} />{error}</div>}

        {loading && (
          <div className="fl-card fl-loading" data-testid="loading-state">
            <div className="fl-spin" />
            <div>
              <div style={{ color: "#fbf7ec", fontWeight: 600 }}>Running the engines & verifying citations…</div>
              <div style={{ fontSize: 13, color: "#9aa1a9" }}>Decomposing elements · checking parties &amp; legislation · scanning contradictions · comparing authorities · weighing options · live-checking every citation.</div>
            </div>
          </div>
        )}

        {!loading && !result && !error && (
          <div className="fl-empty" data-testid="empty-state">
            <div className="ic"><ScanSearch size={30} /></div>
            <h3>Nothing analysed yet</h3>
            <p>Paste a decision above, or <b>try a demo</b> to watch LexGap dismantle a tribunal costs ruling — element by element.</p>
            <p style={{ fontSize: 13, color: "#7f8792" }}>Your result will appear here with an overall verdict, forensic findings, ranked options and a citation audit.</p>
            <div className="fl-legend">
              <span><span className="dot" style={{ background: "#3f7d54" }} />sound / strong</span>
              <span><span className="dot" style={{ background: "#c98a1e" }} />partial / arguable</span>
              <span><span className="dot" style={{ background: "#c0453e" }} />error / weak</span>
              <span><span className="dot" style={{ background: "#6b7178" }} />unclear / needs evidence</span>
            </div>
          </div>
        )}

        {!loading && result && (
          <ResultView
            result={r}
            sectorLabel={sectorLabel}
            analysisId={analysisId}
            firm={firm}
            toolbarExtra={analysisId && (
              <>
                <button className="fl-btn fl-btn--ghost fl-btn--sm" data-testid="share-current-btn" onClick={() => share(analysisId)}><Share2 size={15} /> Copy share link</button>
                <button className="fl-btn fl-btn--ghost fl-btn--sm" data-testid="email-report-btn" onClick={() => setEmailFor(analysisId)}><Mail size={15} /> Email report</button>
                <button className="fl-btn fl-btn--ghost fl-btn--sm" data-testid="open-report-btn" onClick={() => navigate(`/report/${analysisId}`)}><ExternalLink size={15} /> Open shareable page</button>
              </>
            )}
          />
        )}

        <div className="fl-section" data-testid="recent-section">
          <div className="fl-sh">
            <span className="n">—</span>
            <ScanSearch size={16} color="#c9a24b" />
            <h3>Recent audits</h3>
            <span className="sub">tick two or three to compare</span>
          </div>
          {recent.length ? (
            <div className="fl-recent">
              {recent.map((row) => (
                <div className="fl-recentrow" key={row.id} data-testid={`recent-${row.id}`}>
                  <input type="checkbox" className="rr-check" checked={compareSel.includes(row.id)} onChange={() => toggleCompare(row.id)} data-testid={`compare-check-${row.id}`} aria-label="Select to compare" />
                  <a className="rr-title" onClick={() => openRecent(row.id)} data-testid={`recent-open-${row.id}`}>{row.title || "Untitled decision"}</a>
                  <span className="rr-meta">{labelForKey(row.sector)} · {new Date(row.created_at).toLocaleDateString()}</span>
                  <button className="rr-share" onClick={() => share(row.id)} data-testid={`recent-share-${row.id}`} title="Copy share link"><Share2 size={14} /></button>
                </div>
              ))}
            </div>
          ) : <div className="fl-empty-mini" data-testid="recent-empty">No audits yet — your runs will be listed here for quick recall.</div>}
        </div>
      </div>

      {compareSel.length >= 2 && (
        <div className="cmp-bar" data-testid="compare-bar">
          <GitCompareArrows size={17} color="#c9a24b" />
          <span>{compareSel.length} audits selected</span>
          <button className="fl-btn fl-btn--primary fl-btn--sm" data-testid="compare-go-btn" onClick={() => navigate(`/compare?ids=${compareSel.join(",")}`)}>Compare selected</button>
          <button className="fl-btn fl-btn--ghost fl-btn--sm" data-testid="compare-clear-btn" onClick={() => setCompareSel([])}>Clear</button>
        </div>
      )}

      {showBench && (
        <div className="fl-modal-ov" data-testid="benchmarks-modal" onClick={() => setShowBench(false)}>
          <div className="fl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fl-modal-head">
              <div>
                <h3>Legal benchmarks &amp; sources</h3>
                <p>The reference frameworks LexGap audits against for <b>{sectorLabel}</b>. Every source links out for verification.</p>
              </div>
              <button className="fl-modal-close" data-testid="benchmarks-close-btn" onClick={() => setShowBench(false)}><X size={18} /></button>
            </div>
            <div className="fl-benchtabs" data-testid="benchmark-sector-tabs">
              {sectors.map((s) => (
                <button key={s.key} className={`fl-benchtab ${s.key === sector ? "on" : ""}`} data-testid={`benchmark-tab-${s.key}`} onClick={() => setSector(s.key)}>{s.label}</button>
              ))}
            </div>
            <div className="fl-bench">
              {benchmarks.map((b, i) => (
                <a key={i} className="bench" href={b.url} target="_blank" rel="noopener noreferrer" data-testid={`benchmark-${i}`}>
                  <div style={{ flex: 1 }}>
                    <div className="b-name">{b.name}</div>
                    <div className="b-src">{b.src}</div>
                    <div className="b-note">{b.note}</div>
                  </div>
                  <ExternalLink size={16} className="b-ext" />
                </a>
              ))}
            </div>
            <div className="fl-modal-cta">
              <a className="fl-btn fl-btn--primary" data-testid="download-proposal-btn" href={`${API}/proposal/tribunal-equality/pdf`} target="_blank" rel="noopener noreferrer"><FileText size={16} /> Proposal for Tribunal Equality (PDF)</a>
              <a className="fl-btn fl-btn--ghost" data-testid="download-testcase-btn" href={`${API}/testcase/proudman/pdf`} target="_blank" rel="noopener noreferrer"><FileText size={16} /> Proudman Test-Case Evidence (PDF)</a>
            </div>
          </div>
        </div>
      )}

      {showBrand && (
        <div className="fl-modal-ov" data-testid="branding-modal" onClick={() => setShowBrand(false)}>
          <div className="fl-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="fl-modal-head">
              <div>
                <h3>Report branding</h3>
                <p>Add your firm or clinic name — it appears on every report and PDF cover, alongside lexgap.app. Saved in this browser and applied to new audits.</p>
              </div>
              <button className="fl-modal-close" data-testid="branding-close-btn" onClick={() => setShowBrand(false)}><X size={18} /></button>
            </div>
            <label className="fl-label">Firm / clinic name</label>
            <input className="fl-input" data-testid="branding-firm-input" value={firmDraft} onChange={(e) => setFirmDraft(e.target.value)} placeholder="e.g. Doughty Street Chambers" />
            <div className="fl-modal-cta">
              <button className="fl-btn fl-btn--primary" data-testid="branding-save-btn" onClick={saveBranding}>Save branding</button>
            </div>
          </div>
        </div>
      )}

      {emailFor && <EmailModal analysisId={emailFor} onClose={() => setEmailFor(null)} />}
    </div>
  );
}

export default App;
